import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { getSwitchListByNamePromise } from 'apis/FitOnCareAPI'

const FeatureFlagContext = createContext(null)

const mapVersionNumber = (version) =>
  version
    .split('.')
    .map((n, i) => n * Math.pow(10, 3 - i))
    .reduce((p, s) => p + s, 0)

const parseFlagItem = (item, enableVersionCompare) => {
  let versionEnabled = !!item?.status && !!item?.active
  let flagValue = {}

  try {
    flagValue = JSON.parse(item?.value)
    if (enableVersionCompare && versionEnabled && flagValue?.web) {
      versionEnabled =
        mapVersionNumber(flagValue.web) <=
        mapVersionNumber(process.env.REACT_APP_VERSION)
    }
  } catch (e) {
    flagValue = {}
  }

  return {
    enabled: !!item?.active && !!item?.status && versionEnabled,
    flagValue,
  }
}

export const FeatureFlagProvider = ({ children }) => {
  const [flagsByKey, setFlagsByKey] = useState({})
  const [loadedKeys, setLoadedKeys] = useState(() => new Set())
  const loadedKeysRef = useRef(loadedKeys)
  const flagsByKeyRef = useRef(flagsByKey)
  const inflightRequestsRef = useRef(new Map())

  useEffect(() => {
    loadedKeysRef.current = loadedKeys
  }, [loadedKeys])

  useEffect(() => {
    flagsByKeyRef.current = flagsByKey
  }, [flagsByKey])

  const mergeFlags = useCallback((items = []) => {
    if (!items.length) {
      return
    }

    setFlagsByKey((prev) => {
      const next = { ...prev }
      items.forEach((item) => {
        if (item?.name) {
          next[item.name] = item
        }
      })
      return next
    })

    setLoadedKeys((prev) => {
      const next = new Set(prev)
      items.forEach((item) => {
        if (item?.name) {
          next.add(item.name)
        }
      })
      return next
    })
  }, [])

  const fetchFlags = useCallback(
    async (keys, { force = false } = {}) => {
      const uniqueKeys = [...new Set(keys.filter(Boolean))]
      if (!uniqueKeys.length) {
        return []
      }

      // Split keys into cached and missing
      const cachedItems = force
        ? []
        : uniqueKeys
            .filter((key) => loadedKeysRef.current.has(key))
            .map((key) => flagsByKeyRef.current[key])
            .filter(Boolean)

      const keysToFetch = force
        ? uniqueKeys
        : uniqueKeys.filter(
            (key) =>
              !loadedKeysRef.current.has(key) &&
              !inflightRequestsRef.current.has(key)
          )

      if (!keysToFetch.length) {
        const pendingRequests = uniqueKeys
          .map((key) => inflightRequestsRef.current.get(key))
          .filter(Boolean)
        const pendingResults = await Promise.all(pendingRequests)
        return [...cachedItems, ...pendingResults.flat()]
      }

      const request = getSwitchListByNamePromise(keysToFetch)
        .then((items) => {
          mergeFlags(items || [])
          return items || []
        })
        .catch((error) => {
          console.error(error)
          return []
        })
        .finally(() => {
          keysToFetch.forEach((key) => {
            inflightRequestsRef.current.delete(key)
          })
        })

      keysToFetch.forEach((key) => {
        inflightRequestsRef.current.set(key, request)
      })

      const fetchedItems = await request
      return [...cachedItems, ...fetchedItems]
    },
    [mergeFlags]
  )

  const reload = useCallback(
    (keys) => fetchFlags(keys, { force: true }),
    [fetchFlags]
  )

  const value = useMemo(
    () => ({
      flagsByKey,
      loadedKeys,
      fetchFlags,
      reload,
    }),
    [flagsByKey, loadedKeys, fetchFlags, reload]
  )

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  )
}

export const useFeatureFlagContext = () => {
  return useContext(FeatureFlagContext) || {}
}

export const useFeatureFlagByName = ({
  keys,
  enableVersionCompare = false,
  shouldFetch = true,
}) => {
  const { flagsByKey, loadedKeys, fetchFlags, reload } = useFeatureFlagContext()

  const keysKey = keys.join(',')
  useEffect(() => {
    if (shouldFetch && keys?.length) {
      fetchFlags(keys)
    }
    // keysKey is the stable string representation of keys
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchFlags, keysKey, shouldFetch])

  const flags = useMemo(
    () =>
      keys.map((key) => {
        return { key, ...parseFlagItem(flagsByKey[key], enableVersionCompare) }
      }),
    [enableVersionCompare, flagsByKey, keys]
  )

  const reloadFlag = useCallback(() => reload(keys), [keys, reload])

  return {
    loaded: keys.every((key) => loadedKeys.has(key)),
    reload: reloadFlag,
    flags,
  }
}

export const useAssessmentFlag = () => {
  const { flags, loaded } = useFeatureFlagByName({
    keys: ['pain_activity_assessment_enabled'],
  })
  const showAssessment = flags.find(
    (flag) => flag.key === 'pain_activity_assessment_enabled'
  )?.enabled
  return { showAssessment, loaded }
}
